import { useEffect, useState } from "react";
import moment from "moment";
import { requestType } from "../services/requests";
import { apiRequest, getUserDetails } from "../services/apiRequest";

/**
 * Shared "Data last updated" timestamp used by the Royal Bridge and Royal Momentum
 * footers.
 *
 * The value is a property of the underlying data, not of the login event, so it
 * is fetched on mount (a full page load / refresh always re-fetches) and again
 * whenever the tab regains focus — covering the "left logged in and refreshed
 * the next day" case without any polling.
 *
 * Because the footer remounts on every route change, the raw value is cached at
 * module level and fetches are de-duped, so navigating around does not hammer
 * the endpoint. A hard refresh resets the cache and re-fetches.
 */

let cachedValue = null; // last raw "YYYY-MM-DD HH:mm:ss" string
let inFlight = null; // de-dupes concurrent fetches
const subscribers = new Set();

/**
 * Zone that BE's naive "YYYY-MM-DD HH:mm:ss" timestamps are expressed in — the
 * UK server's own clock, which is GMT in winter and BST (+1) in summer, so the
 * offset cannot be hardcoded.
 *
 * ⚠️ If BE moves to UTC while still sending a naive string, set this to "UTC".
 * That is the only edit required. Better still: ask BE for ISO-8601 with an
 * offset ("2026-07-31T07:55:00Z") — such strings pin their own instant and skip
 * this constant entirely, making the FE correct for any zone BE picks.
 */
const BE_TIMEZONE = "Europe/London";

/** True for strings that carry their own offset, e.g. "…Z" or "…+01:00". */
const hasExplicitOffset = (value) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);

/**
 * How far `timeZone` sits from UTC at a given instant. Derived by rendering the
 * instant as that zone's wall clock and reading it back as UTC — the gap is the
 * offset, DST included, with no timezone database to ship.
 */
const zoneOffsetMs = (instantMs, timeZone) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).formatToParts(new Date(instantMs));

    const get = (type) => Number(parts.find((part) => part.type === type).value);
    // Some engines render midnight as hour 24; the date part already advanced.
    const asUtc = Date.UTC(
        get("year"),
        get("month") - 1,
        get("day"),
        get("hour") % 24,
        get("minute"),
        get("second")
    );

    return asUtc - instantMs;
};

/** Resolves a wall clock reading in `timeZone` to a real UTC instant. */
const wallClockToInstant = (wallClockMs, timeZone) => {
    const naiveOffset = zoneOffsetMs(wallClockMs, timeZone);
    const candidate = wallClockMs - naiveOffset;

    // Across a DST boundary the offset at the true instant differs from the one
    // guessed above, so settle a second time.
    const trueOffset = zoneOffsetMs(candidate, timeZone);
    return trueOffset === naiveOffset ? candidate : wallClockMs - trueOffset;
};

export const parseInstant = (value) => {
    const raw = String(value).trim();

    // Self-describing — the string already pins the instant, so BE's zone is
    // irrelevant and nothing here needs to change if BE switches to UTC.
    if (hasExplicitOffset(raw)) {
        const pinned = moment.parseZone(raw);
        return pinned.isValid() ? pinned : null;
    }

    // Naive wall clock, in BE_TIMEZONE. Read as UTC first to get the raw
    // reading, then shift it by that zone's offset.
    const naive = moment.utc(raw, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss"], true);
    if (!naive.isValid()) return null;

    try {
        return moment(wallClockToInstant(naive.valueOf(), BE_TIMEZONE));
    } catch {
        return naive; // Intl rejected the zone — show the unshifted reading
    }
};

const notify = (value) => {
    cachedValue = value;
    subscribers.forEach((cb) => cb(value));
};

const fetchLastUpdated = () => {
    if (inFlight) return inFlight;

    const uid = getUserDetails()?.user_details?.uid;
    if (!uid) return Promise.resolve(null);

    inFlight = apiRequest({
        payload: {
            data: {
                user_uid: uid,
            },
        },
        requestType: requestType.getDataLastUpdated,
    })
        .then((res) => {
            const value = res?.data?.data?.last_updated ?? null;
            notify(value);
            return value;
        })
        .catch(() => null)
        .finally(() => {
            inFlight = null;
        });

    return inFlight;
};

/**
 * @returns {string} A pre-formatted timestamp ready to render, or "" while the
 * value is still loading / unavailable.
 */
export const useDataLastUpdated = () => {
    const [value, setValue] = useState(cachedValue);

    useEffect(() => {
        subscribers.add(setValue);

        // Fetch on mount; reuse the module cache if a previous footer already got it.
        if (cachedValue == null) {
            fetchLastUpdated();
        } else {
            setValue(cachedValue);
        }

        // Refetch when the tab regains focus / becomes visible again.
        const onFocus = () => {
            if (document.visibilityState === "visible") {
                fetchLastUpdated();
            }
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);

        return () => {
            subscribers.delete(setValue);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, []);

    // Show an ellipsis placeholder while the request is pending / before the
    // value resolves, so the footer never renders an empty timestamp.
    if (!value) return "…";

    const parsed = parseInstant(value);
    if (!parsed) return value; // unrecognised shape — show it raw rather than "Invalid date"

    return parsed.local().format("DD MMM YYYY HH:mm");
};
